import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_KEY = 'vibesprint_tour_completed';

const TOUR_STEPS = [
  {
    element: '[data-tour="workspace-switcher"]',
    popover: {
      title: '📦 Your workspace',
      description:
        "Every product you're working on gets its own workspace. Switch between them here — signals and decisions stay isolated per product.",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="usage-meter"]',
    popover: {
      title: '⚡ Your AI analyses',
      description:
        "Each batch of feedback the AI processes counts as one analysis. You're on the Demo plan with 10/month — plenty to see the value. Upgrade anytime.",
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="lanes"]',
    popover: {
      title: '🚀 The three lanes',
      description:
        "This is the magic: every signal goes into Build Next (💰 revenue), Fix Now (⚠️ churn) or Ignore (🔇 noise). The AI decides. You ship.",
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-tour="import-button"]',
    popover: {
      title: '📥 Add signals manually',
      description:
        "Paste tweets, Discord, email, CSV — anything. The AI auto-detects the format and categorizes within seconds. Automated Signal Sources (Reddit, HN, RSS) are available from the Signal Sources page.",
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-tour="sample-data"]',
    popover: {
      title: '🎬 Or try a sample dataset',
      description:
        "Want to see VibeSprint in action without pasting your own feedback? Click 'Try sample data' on any empty lane and we'll load 13 curated indie-hacker signals.",
      side: 'top',
      align: 'center',
    },
  },
  {
    popover: {
      title: "🎉 You're set",
      description:
        "That's it — you know enough to start. We'll be here when you need pricing, FAQ, or support. Ship faster!",
    },
  },
];

const ProductTour = ({ autoStart = true }) => {
  const location = useLocation();
  const driverRef = useRef(null);

  const start = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    const d = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      stagePadding: 6,
      stageRadius: 12,
      overlayColor: 'rgba(15, 8, 25, 0.65)',
      progressText: 'Step {{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: "Let's go 🚀",
      steps: TOUR_STEPS,
      onDestroyed: () => {
        try {
          localStorage.setItem(TOUR_KEY, 'true');
        } catch (e) {
          /* ignore */
        }
      },
    });
    driverRef.current = d;
    d.drive();
  }, []);

  // Auto-start on first dashboard visit
  useEffect(() => {
    if (!autoStart) return;
    if (location.pathname !== '/app') return;

    let completed = null;
    try {
      completed = localStorage.getItem(TOUR_KEY);
    } catch (e) {
      /* localStorage blocked */
    }
    if (completed) return;

    // Wait for dashboard tour targets to render
    const t = setTimeout(() => {
      const target = document.querySelector('[data-tour="workspace-switcher"]');
      if (target) start();
    }, 1200);
    return () => clearTimeout(t);
  }, [autoStart, location.pathname, start]);

  // Expose imperative start so other components can trigger
  useEffect(() => {
    window.__vibesprintStartTour = () => {
      try {
        localStorage.removeItem(TOUR_KEY);
      } catch (e) {
        /* ignore */
      }
      start();
    };
    return () => {
      delete window.__vibesprintStartTour;
    };
  }, [start]);

  return null;
};

export const startProductTour = () => {
  if (typeof window !== 'undefined' && window.__vibesprintStartTour) {
    window.__vibesprintStartTour();
  }
};

export default ProductTour;
